import React, { useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import { MatriculaAlunoSchema } from "@/schema/matriculaAlunoSchema";
import { Button, Divider, Row } from "antd";
import Logomarca from "@/assets/images/Logo.png";
import Socrates from "@/assets/images/socrates_orcamento.png";
import html2canvas from 'html2canvas';
import { Container } from './styles';
import { getTurmaById } from '@/services/turma';

export interface DisciplinaTurma {
    cod: string;
    nome: string;
}

export interface ParcelasInfo {
    qtdParcelas: number;
    totalSemDesconto: number;
    parcelaSemDesconto: number;
    totalComDesconto: number;
    parcelaComDesconto: number;
}

interface OrcamentoProps {
    matriculaAluno: MatriculaAlunoSchema;
    parcelasInfo?: ParcelasInfo | null;
    /**
     * Grade da turma já carregada pelo chamador. A página pública de orçamento
     * passa a dela: o GET Turma/{id} abaixo vai pela instância autenticada e
     * responderia 401 para um visitante sem sessão.
     */
    disciplinasTurma?: DisciplinaTurma[];
}

const formatCurrency = (value: number) =>
    value.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' });

const formatDate = (date: Date) =>
    date.toLocaleDateString('pt-br', { day: '2-digit', month: '2-digit', year: 'numeric' });

export const Orcamento = ({ matriculaAluno, parcelasInfo, disciplinasTurma: disciplinasTurmaProp }: OrcamentoProps) => {

    const [disciplinasTurma, setDisciplinasTurma] = useState<DisciplinaTurma[]>([]);

    useEffect(() => {
        // Grade recebida pronta: não há o que buscar.
        if (disciplinasTurmaProp) {
            setDisciplinasTurma(disciplinasTurmaProp);
            return;
        }

        const turmaId = matriculaAluno.turmaId;
        if (!turmaId) return;

        getTurmaById(String(turmaId)).then((data: any) => {
            const lista: DisciplinaTurma[] = [];
            data?.data?.especificas?.forEach((e: any) => {
                lista.push({ cod: `E${e.id}`, nome: e.nome });
            });
            data?.data?.servicos?.forEach((s: any) => {
                lista.push({ cod: `S${s.id}`, nome: s.nome });
            });
            lista.sort((a, b) => a.nome.localeCompare(b.nome));
            setDisciplinasTurma(lista);
        });
    }, [matriculaAluno.turmaId, disciplinasTurmaProp]);

    // Conjunto de códigos selecionados no orçamento
    const codigosSelecionados = new Set(
        (matriculaAluno.disciplinas ?? []).map(d => d.codEspecificaServico).filter(Boolean)
    );

    const getValorTotalComDesconto = () => {
        return matriculaAluno.disciplinas.reduce((a, v) => a + (v.valorFinal || 0), 0);
    };

    const getValorTotalSemDesconto = () => {
        return matriculaAluno.disciplinas.reduce((a, v) => a + (v.valor || 0), 0);
    };

    const getTextoCondicaoPagamento = (
        prefixo: 'De' | 'Por',
        total: number,
        qtdParcelas: number,
        valorParcela: number,
    ) => {
        if (qtdParcelas <= 1) {
            return `${prefixo} ${formatCurrency(total)} à Vista`;
        }

        return `${prefixo} ${qtdParcelas} parcelas de ${formatCurrency(valorParcela)}`;
    };



    const printDocument = () => {
        const input = document.getElementById('containerOrcamento');

        const fileName = `ORCAMENTO_${matriculaAluno.estudante?.pessoaEstudante?.nome?.toUpperCase() ?? 'ALUNO'}_${formatDate(new Date())}.pdf`;

        if (input) {
            html2canvas(input, { scale: 2, backgroundColor: '#ffffff' })
                .then((canvas) => {
                    const imgData = canvas.toDataURL('image/png');
                    const pdf = new jsPDF('p', 'mm', 'a4');
                    const imgProps = pdf.getImageProperties(imgData);
                    const pdfWidth = pdf.internal.pageSize.getWidth();
                    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                    pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
                    pdf.save(fileName);
                });
        }
    };

    // Distribui as disciplinas da turma em uma matriz com 3 colunas (coluna a coluna)
    const numColunas = 3;
    const numLinhas = Math.ceil(disciplinasTurma.length / numColunas);
    const linhasDisciplinas: (DisciplinaTurma | null)[][] = [];
    for (let i = 0; i < numLinhas; i++) {
        const linha: (DisciplinaTurma | null)[] = [];
        for (let j = 0; j < numColunas; j++) {
            const idx = j * numLinhas + i;
            linha.push(disciplinasTurma[idx] ?? null);
        }
        linhasDisciplinas.push(linha);
    }

    const responsavelNome = matriculaAluno.responsavel?.pessoaResponsavel?.nome ?? '';
    const alunoNome = matriculaAluno.estudante?.pessoaEstudante?.nome ?? '';
    const turmaNome = matriculaAluno.turmaNome ?? '';

    const cellBorder: React.CSSProperties = {
        border: '1px solid #000',
        padding: '6px 10px',
        verticalAlign: 'middle',
    };

    return (
        <>
            <div id="containerOrcamento">
                <Container>
                    {/* Cabeçalho: Logo + Título ORÇAMENTO */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '20%', padding: '6px 0px', verticalAlign: 'middle' }}>
                                    <img src={Logomarca} alt="Logo" style={{ height: 90, objectFit: 'contain' }} />
                                </td>
                                <td style={{
                                    width: '60%',
                                    padding: '6px 10px',
                                    verticalAlign: 'middle',
                                    color: '#000',
                                    position: 'relative',
                                }}>
                                    <div style={{
                                        textAlign: 'center',
                                        fontSize: 28,
                                        fontWeight: 'bold',
                                    }}>
                                        ORÇAMENTO {matriculaAluno.unidade?.nome?.toUpperCase() ?? ''}
                                    </div>
                                    </td>
                                <td style={{
                                    width: '10%',
                                    padding: '6px 10px',
                                    verticalAlign: 'middle',
                                    color: '#000',
                                    position: 'relative',
                                }}>
                                    <div style={{
                                        right: 28,
                                        fontSize: 28,
                                        fontWeight: 'bold',
                                    }}>
                                        {formatDate(new Date())}
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ fontSize: 18 }}>

                    {/* Dados de Responsável e Aluno */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                        <tbody>
                            <tr>
                                <td style={{ ...cellBorder, width: '15%', fontWeight: 'bold' }}>RESPONSÁVEL:</td>
                                <td style={cellBorder}>{responsavelNome}</td>
                            </tr>
                            <tr>
                                <td style={{ ...cellBorder, fontWeight: 'bold' }}>ALUNO:</td>
                                <td style={cellBorder}>{alunoNome}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Turma */}
                    { turmaNome && (
                        <p style={{ textAlign: 'center', margin: '12px 0', color: '#000', fontWeight: 500 }}>
                            TURMA {String(turmaNome).toUpperCase()}
                        </p>
                    )}

                    {/* Disciplinas em grid de 3 colunas com X */}
                    {disciplinasTurma.length > 0 && (
                        <table style={{ width: '90%', borderCollapse: 'collapse', margin: '0 auto 20px' }}>
                            <tbody>
                                {linhasDisciplinas.map((linha, i) => (
                                    <tr key={i}>
                                        {linha.map((disc, j) => {
                                            const selecionada = disc ? codigosSelecionados.has(disc.cod) : false;
                                            return (
                                                <React.Fragment key={`d-${i}-${j}`}>
                                                    <td style={{
                                                        ...cellBorder,
                                                        width: '4%',
                                                        textAlign: 'center',
                                                        fontWeight: 'bold',
                                                    }}>
                                                        {selecionada ? 'X' : ''}
                                                    </td>
                                                    <td style={{
                                                        ...cellBorder,
                                                        width: '29%',
                                                    }}>
                                                        {disc?.nome ?? ''}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {/* Linha grossa antes da proposta */}
                    <div style={{ borderTop: '3px solid #000', margin: '20px 0' }}></div>

                    {/* Valor da proposta */}
                    <table style={{ borderCollapse: 'collapse', margin: '20px auto', textAlign: 'center', width: '100%' }}>
                        <tbody>
                            <tr>
                                <td style={{ fontWeight: 'bold', fontSize: 20, paddingBottom: 6 }}>
                                    VALOR DA PROPOSTA
                                </td>
                            </tr>
                            <tr>
                                <td style={{ fontSize: 18 }}>
                                    {getTextoCondicaoPagamento(
                                        'De',
                                        parcelasInfo?.totalSemDesconto ?? getValorTotalSemDesconto(),
                                        parcelasInfo?.qtdParcelas ?? 1,
                                        parcelasInfo?.parcelaSemDesconto ?? getValorTotalSemDesconto(),
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td style={{ fontSize: 18 }}>
                                    {getTextoCondicaoPagamento(
                                        'Por',
                                        parcelasInfo?.totalComDesconto ?? getValorTotalComDesconto(),
                                        parcelasInfo?.qtdParcelas ?? 1,
                                        parcelasInfo?.parcelaComDesconto ?? getValorTotalComDesconto(),
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Linha grossa depois da proposta */}
                    <div style={{ borderBottom: '3px solid #000', margin: '20px 0' }}></div>

                    {/* Observação */}
                    <p style={{ color: '#000', margin: '24px 0 12px' }}>
                        OBS: Esse orçamento tem validade de ____ dias.
                    </p>

                    {/* Texto institucional com imagem flutuante */}
                    <div style={{ overflow: 'hidden', color: '#000' }}>
                        <p style={{ textAlign: 'justify', fontSize: 18, margin: '0 0 8px' }}>
                            Acreditamos que a aprovação é resultado de disciplina e método, não de sorte.
                        </p>
                        <p style={{ textAlign: 'justify', fontSize: 18, margin: '0 0 8px' }}>
                            Na Academia das Específicas, unimos o seu esforço à nossa metodologia de excelência
                            para construir, passo a passo, uma trajetória sólida rumo ao sucesso.
                        </p>
                        <p style={{ textAlign: 'justify', fontSize: 18, margin: '0 0 8px' }}>
                            Cada detalhe do curso é planejado para que o estudante tenha segurança e foco no
                            que realmente importa.
                        </p>
                        <p style={{ textAlign: 'justify', fontSize: 18, margin: '0 0 8px' }}>
                            Valorize seu tempo e confie em quem é especialista em aprovação.
                        </p>
                        <p style={{ textAlign: 'justify', fontSize: 18, margin: '0 0 8px' }}>
                            Será uma honra contribuir para sua jornada e dividir com você a alegria dessa
                            grande vitória.
                        </p>
                        <br />
                        <p style={{ textAlign: 'left', fontSize: 18, margin: '0 0 8px' }}>
                            Orçamento gerado por <strong>{matriculaAluno.usuarioNome}</strong>
                        </p>
                    </div>
                    </div>
                </Container>
            </div>
            <Divider />
            <Row justify="center">
                <Button type="primary" onClick={printDocument}>Imprimir</Button>
            </Row>
        </>
    );
};